import React, {useContext, useState, useEffect, useCallback} from 'react'
import {Row, Col, H6, Paragraph, withTooltip, OverdueIcon, Strong, Body} from '@modifi/ui'
import {useTranslation, Trans} from '@modifi/plugin-i18n'
import {FormDataContext} from '@modifi/plugin-forms'
import {InputPureWithInputContainer as InputPure} from '@modifi/plugin-onboarding'
import {Currency} from '@modifi/enums'
import {airBlue} from '@modifi/colors'

import {FormInputComponentCol} from '../../../../../plugins/onboarding/components'
import {SelectOption} from '../../../../../types/common'

const TooltipIcon = withTooltip(OverdueIcon) as React.ComponentType<any>

export type IntermediaryBankDetailsProps = {
  availableCurrencies: SelectOption[]
  bankName?: string
  fetchBankDetails: (params: {swiftCode: string}) => void
  bankUtilsErrorMessage?: string
  resetBankUtils: () => void
  persistedIntermediaryBankName?: string
}

const IntermediaryBankDetails: React.FC<IntermediaryBankDetailsProps> = ({
  availableCurrencies,
  bankName = '',
  fetchBankDetails,
  bankUtilsErrorMessage = '',
  resetBankUtils,
  persistedIntermediaryBankName = '',
}) => {
  const {t} = useTranslation('charon')
  const {data} = useContext(FormDataContext)
  const [isUSDCurrencySelected, setIsUSDCurrencySelected] = useState(false)
  const [prevBankCode, setPrevBankCode] = useState(data?.intermediaryBankCode as string)

  useEffect(() => {
    resetBankUtils()
  }, [resetBankUtils])

  useEffect(() => {
    const uSDExternalId =
      availableCurrencies.find(currencyOption => currencyOption?.label === Currency.USD)?.value ||
      ''
    // @ts-expect-error type not generated properly
    if (data?.['sellerBankDetails.currency'].includes(uSDExternalId)) {
      setIsUSDCurrencySelected(true)
    } else {
      setIsUSDCurrencySelected(false)
    }
  }, [data, availableCurrencies])

  const handleBankCodeChanges = useCallback(
    (evt: React.UIEvent, changedVal: string) => {
      const newLength = changedVal.length
      const prevLength = prevBankCode?.length

      // only 8 and 11 digit SWIFT codes are valid
      // hence validating when 8 or 11 digits entered and when changed from 8 or 11
      if (
        [8, 11].includes(newLength) ||
        ([8, 11].includes(prevLength) && [7, 9, 10, 12].includes(newLength))
      ) {
        fetchBankDetails({swiftCode: changedVal})
      }
      // clearing validation because value is optional
      if (newLength === 0) {
        resetBankUtils()
      }
      setPrevBankCode(changedVal)
    },
    [fetchBankDetails, prevBankCode, resetBankUtils]
  )

  if (!isUSDCurrencySelected) {
    return null
  }

  return (
    <>
      <Row>
        <Col>
          <H6>
            {t('seller-bank-account.intermediary-bank-details.headline', {ns: 'charon'})}{' '}
            <TooltipIcon
              tooltipContent={
                <Trans
                  i18nKey="seller-bank-account.intermediary-bank-details.tooltip-content"
                  t={t}
                  ns="charon"
                  components={{n: <br />}}
                />
              }
              tooltipTheme="admin"
              tooltipPlacement="left"
              color={airBlue}
            />
          </H6>
        </Col>
      </Row>
      <Row mb={16}>
        <Col col="auto">
          <H6>{t('seller-bank-account.USD-intermediary-bank-details.headline')}</H6>
        </Col>
        <Col col="auto">
          <Paragraph>
            {t('seller-bank-account.USD-intermediary-bank-details.optional-text.label')}
          </Paragraph>
        </Col>
      </Row>
      <Row>
        <FormInputComponentCol
          t={t}
          isAlwaysVisible
          inputComponent={InputPure}
          inputName="intermediaryBankCode"
          inputProps={{
            required: false,
            placeholder: t('seller-bank-account.intermediaryBankCode.placeholder'),
            label: t('seller-bank-account.intermediaryBankCode.label'),
            onChange: handleBankCodeChanges,
            hasError: !!bankUtilsErrorMessage,
            isValid: !bankUtilsErrorMessage,
          }}
          errorMessage={bankUtilsErrorMessage}
          isValid={!bankUtilsErrorMessage}
        />
      </Row>
      <Row mb={24}>
        <Col lg={6}>
          <Row>
            <Col>
              <Strong>{t('seller-bank-account.intermediary-bank-details.bank-name.label')}</Strong>
            </Col>
          </Row>
          <Row mt={16}>
            <Col>
              <Body>{bankName || persistedIntermediaryBankName || '-'}</Body>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}

export default IntermediaryBankDetails
